import React from 'react';
import { useParams } from 'react-router';
import { CandidateOpportunitiesConst } from '@axiom/const';
import {
  Badge,
  BadRequestError,
  ParagraphHeader,
  IconButton,
  ImageCircle,
  Layout,
  LayoutItem,
  TabsItem,
  Tabs,
  Gutter,
  Toggle,
  Tooltip,
  CondensedHeader,
  CondensedLarge,
  CondensedMedium,
  Flashy,
  Icon,
  CandidateProfileUtil,
  Card,
  CardHeader,
  CardSection,
  Form,
  Suspense,
  ToastUtil,
} from '@axiom/ui';
import type {
  AhqSubmission,
  CandidateReputationClarificationPatch,
  Opportunity,
  OpportunityCandidate,
} from '@axiom/validation';
import type { FormikHelpers } from '@axiom/ui';

import { SubmissionsLegacyApi } from '../../api/submissions-legacy-api';
import { CpqMarginIndicator } from '../CpqMarginIndicator/CpqMarginIndicator';
import { CandidateUtil } from '../../utils/candidate-util';

import { OpportunitySubmissionCandidateExperiences } from './OpportunitySubmissionCandidateExperiences';
import { OpportunitySubmissionCandidateRate } from './OpportunitySubmissionCandidateRate';
import { OpportunitySubmissionCandidateReputation } from './OpportunitySubmissionCandidateReputation';
import { OpportunitySubmissionUtil } from './opportunity-submission-util';
import {
  OpportunitySubmissionSelectedCandidateSchema,
  type SelectedCandidate,
} from './opportunity-submission-selected-candidate-schema';

const { AllStatusLabels: AllStatusLabelsConst } = CandidateOpportunitiesConst;
const AllStatusLabels: Record<string, string> = AllStatusLabelsConst;

export type OpportunitySubmissionSelectedCandidateProps = {
  candidate: OpportunityCandidate;
  initialCandidate: OpportunityCandidate;
  opportunity: Opportunity;
  readOnly: boolean;
  submission: AhqSubmission;
  candidates: OpportunityCandidate[];
  candidateReputationClarificationPatchCall: CandidateReputationClarificationPatch[];
  onSubmissionDescriptionSave?: (
    candidateId: string,
    description: string
  ) => void;
  name?: string;
};

const TAB_MAP: Record<string, string> = {
  rate: 'Rate',
  feedback: 'Feedback',
};

export const OpportunitySubmissionSelectedCandidate = ({
  initialCandidate,
  submission,
  candidate,
  opportunity,
  readOnly,
  candidates,
  candidateReputationClarificationPatchCall,
  onSubmissionDescriptionSave,
  name = 'SELECTED_CANDIDATE',
}: OpportunitySubmissionSelectedCandidateProps) => {
  const { tab } = useParams<{ tab?: string }>();

  const startTab = (tab && TAB_MAP[tab]) || 'Experience';

  const submissionCandidateExperiences =
    submission.candidateOpportunities.find(
      co => co.candidate.id === candidate.id
    )?.candidate.experiences ?? [];

  const experiences = (candidate.experiences ?? []).map(exp => {
    const submissionExp = submissionCandidateExperiences.find(
      se => se.id === exp.id
    );
    return submissionExp ? { ...exp, ...submissionExp } : exp;
  });

  const handleToggleSubmit = async (
    values: SelectedCandidate,
    formActions: FormikHelpers<SelectedCandidate>
  ) => {
    const isActivating = values.isActiveForSubmission === true;

    const rankPatches = OpportunitySubmissionUtil.buildSubmissionRankPatches({
      candidates,
      opportunity,
      activatingCandidateId: isActivating ? candidate.id : undefined,
      deactivatingCandidateId: isActivating ? undefined : candidate.id,
    });

    const otherRankPatches = rankPatches.filter(
      p => p.candidateId !== candidate.id
    );
    const toggledRankPatch = rankPatches.find(
      p => p.candidateId === candidate.id
    );

    const toggledCandidatePatch = {
      candidateId: candidate.id,
      isActiveForSubmission: values.isActiveForSubmission ?? undefined,
      // Only send highlightedExperiences when activating. Deactivating
      // must not overwrite the backend's stored per-experience visibility,
      // otherwise hidden experiences become visible when the talent is
      // re-included on the submission later (BOB-324).
      ...(isActivating
        ? {
            highlightedExperiences: experiences.map(exp => ({
              id: exp.id,
              experienceVisible: exp.experienceVisible,
              isHighlighted: exp.isHighlighted,
              submissionDescription: exp.submissionDescription,
            })),
          }
        : {}),
      submissionRank: toggledRankPatch?.submissionRank ?? null,
    };

    try {
      await SubmissionsLegacyApi.updateSubmission(submission.submissionId, {
        newSubmissionType: submission.newSubmissionType,
        candidates: [...otherRankPatches, toggledCandidatePatch],
      });
      await OpportunitySubmissionUtil.refreshSubmissionAndCandidates({
        submissionId: submission.submissionId,
        opportunityId: opportunity.id,
        candidateId: candidate.id,
      });
    } catch (error) {
      if (error instanceof BadRequestError) {
        formActions.resetForm();
        ToastUtil.add({
          dismissible: true,
          name: 'submission-placeholder-error',
          type: 'error',
          children: (
            <>
              <CondensedHeader name="SUBMISSION_PLACEHOLDER_ERROR_HEADER">
                Unable to include talent in submission
              </CondensedHeader>
              <CondensedLarge name="SUBMISSION_PLACEHOLDER_ERROR_MESSAGE">
                {String((error.getErrors() as { message?: string }).message)}
              </CondensedLarge>
            </>
          ),
        });
      } else {
        throw error;
      }
    }
  };

  const errorText =
    OpportunitySubmissionUtil.getAllowedToSubmitCandidateErrorText(
      initialCandidate
    );

  return (
    <div data-test={name}>
      <Card>
        <CardHeader>
          <Layout position="middle space-between">
            <LayoutItem>
              <Layout position="middle">
                <LayoutItem rightGutter="16px">
                  <ImageCircle
                    size="default"
                    src={CandidateProfileUtil.getProfileImageUri(
                      {
                        profileImageKey: candidate.profileImageKey ?? null,
                      },
                      'W_100'
                    )}
                    imageName={candidate.calculatedDisplayName}
                  />
                </LayoutItem>
                <LayoutItem>
                  <ParagraphHeader maxLines={1}>
                    {candidate.calculatedDisplayName}
                  </ParagraphHeader>
                </LayoutItem>
              </Layout>
            </LayoutItem>
            <LayoutItem>
              <Layout position="middle" horizontalGutter="8px">
                <CpqMarginIndicator
                  name="CPQ_MARGIN_INDICATOR"
                  candidateOpportunity={initialCandidate}
                />
                {candidate.isPriorityTalent ? (
                  <Layout position="middle">
                    <Badge
                      background="forest green"
                      name="PENDO-PRIORITY-TALENT-BADGE"
                    >
                      Priority
                    </Badge>
                    <Tooltip>
                      Priority Talent have been on the Beach for over 6 weeks (2
                      weeks for Asia) or on the Waitlist for over 12 weeks (8
                      weeks for UK, 4 weeks for Asia) and are available for
                      opportunities. They can be priced at lower margins to give
                      cost savings to the client.
                    </Tooltip>
                  </Layout>
                ) : null}
                <Badge
                  name="CANDIDATE_STATUS_BADGE"
                  background={CandidateUtil.getCandidateStatusBadgeColor(
                    candidate.candidateStatus
                  )}
                >
                  {candidate.candidateStatus
                    ? AllStatusLabels[candidate.candidateStatus]
                    : null}
                </Badge>
                {CandidateUtil.isSubmittedFromWorkFeed(
                  candidate,
                  opportunity
                ) ? (
                  <Badge name="self-submit-badge" background="gray">
                    Self-Submit
                  </Badge>
                ) : null}
                <IconButton
                  icon="pop-out"
                  pattern="secondary"
                  variation="minimal"
                  toTab={`/talent-detail/${candidate.id}`}
                  title={`Open "${candidate.calculatedDisplayName}" in new tab`}
                />
              </Layout>
            </LayoutItem>
          </Layout>
        </CardHeader>
        <CardSection>
          <Gutter bottom="8px">
            <Layout position="middle space-between">
              <LayoutItem>
                <Form
                  schema={OpportunitySubmissionSelectedCandidateSchema}
                  initialValues={{
                    isActiveForSubmission: candidate.isActiveForSubmission,
                  }}
                  onSubmit={handleToggleSubmit}
                  submitOnChange={!readOnly}
                >
                  {() => (
                    <Toggle
                      name="isActiveForSubmission"
                      label="Include Talent in Submission"
                      disabled={!!errorText || readOnly}
                      variation="minimal"
                    />
                  )}
                </Form>
              </LayoutItem>
              <LayoutItem>
                {errorText && !readOnly ? (
                  <Layout name="CANDIDATE_ERROR_TEXT">
                    <Flashy color="textAlert">
                      <Icon name="alert-filled" />
                    </Flashy>
                    <Gutter left="8px" />
                    <CondensedMedium>{errorText}</CondensedMedium>
                  </Layout>
                ) : null}
              </LayoutItem>
            </Layout>
          </Gutter>
        </CardSection>
      </Card>
      <Gutter bottom="16px" />
      <Tabs name="CANDIDATE_TABS" startTab={startTab}>
        <TabsItem label="Experience" name="Experience">
          <Gutter bottom="8px" />
          <Card>
            <CardSection>
              <OpportunitySubmissionCandidateExperiences
                name="CANDIDATE_EXPERIENCES"
                candidate={candidate}
                experiences={experiences}
                opportunity={opportunity}
                submission={submission}
                onSubmissionDescriptionSave={onSubmissionDescriptionSave}
                readOnly={readOnly}
              />
            </CardSection>
          </Card>
        </TabsItem>
        <TabsItem label="Rate" name="Rate">
          <Suspense>
            <Gutter bottom="8px" />
            <Card>
              <CardSection>
                <OpportunitySubmissionCandidateRate
                  key={candidate.id}
                  name="CANDIDATE_RATE"
                  submission={submission}
                  opportunityId={opportunity.id}
                  readOnly={readOnly}
                  candidateId={candidate.id}
                />
              </CardSection>
            </Card>
          </Suspense>
        </TabsItem>
        {candidate.isActiveForSubmission ? (
          <TabsItem label="Feedback" name="Feedback">
            <Gutter bottom="8px" />
            <Card>
              <CardSection>
                <OpportunitySubmissionCandidateReputation
                  name="CANDIDATE_REPUTATION"
                  submission={submission}
                  opportunity={opportunity}
                  candidateReputationClarificationPatchCall={
                    candidateReputationClarificationPatchCall
                  }
                  candidate={candidate}
                  readOnly={readOnly}
                />
              </CardSection>
            </Card>
          </TabsItem>
        ) : null}
      </Tabs>
    </div>
  );
};
